import { BATCH_LABELS_WS_KEYS } from "@wingspanhq/payments/dist/lib/constants";
import { Batch } from "@wingspanhq/wingspan-api-sdk";

export enum ImportBatchEventType {
  BatchCreated = "BatchCreated",
  UploadCompleted = "UploadCompleted",
  ImportStarted = "ImportStarted",
  ImportCompleted = "ImportCompleted"
}

export type ImportBatchEvent = {
  eventType: ImportBatchEventType;
  timestamp: Date;
};

const toDate = (value?: Date | string | null): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? undefined : date;
};

/**
 * Milestones of an import batch, most recent first.
 *
 * v1 recorded a dedicated event log; v3 keeps the same milestones as timestamps
 * on `Batch.events`, with the end of the spreadsheet upload in the
 * `uploadFinishedAt` metadata the Flatfile listener writes.
 */
export const selectorImportBatchTimelineEvents = (
  batch?: Batch
): ImportBatchEvent[] => {
  if (!batch) {
    return [];
  }

  const candidates: Array<[ImportBatchEventType, Date | undefined]> = [
    [ImportBatchEventType.BatchCreated, toDate(batch.events?.createdAt)],
    [
      ImportBatchEventType.UploadCompleted,
      toDate(batch.metadata?.[BATCH_LABELS_WS_KEYS.uploadFinishedAt])
    ],
    [
      ImportBatchEventType.ImportStarted,
      toDate(batch.events?.pendingAt ?? batch.events?.processingAt)
    ],
    [
      ImportBatchEventType.ImportCompleted,
      toDate(batch.events?.completedAt ?? batch.events?.failedAt)
    ]
  ];

  return candidates
    .filter((entry): entry is [ImportBatchEventType, Date] => !!entry[1])
    .map(([eventType, timestamp]) => ({ eventType, timestamp }))
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};
